import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Collapse,
  FormControlLabel,
  Checkbox,
  Switch,
  alpha
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  FileText,
  HelpCircle,
  X,
  GripVertical,
  Plus,
  Trash2,
  Wand2,
  ChevronDown,
  ChevronRight,
  Image,
  CheckCircle,
  AlertCircle,
  Link,
  Link2,
  Eye
} from 'lucide-react';
import PageHeader from './PageHeader';
import ImageControls from './ImageControls';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import WordPressClient from '../utils/WordPressClient';
import aiClient from '../api/clients/AiClient';
import imageClient from '../api/clients/ImageClient';
import { formatContent } from '../utils/contentFormatting';
import { useAIModels } from '../hooks/useAIModels';
import ModelSelector from './ModelSelector';

// Constants
const PRIMARY_COLOR = '#E04C16';
const SECONDARY_COLOR = '#FF6B3D';
const BORDER_COLOR = 'rgba(0, 0, 0, 0.12)';
const CARD_BACKGROUND = 'rgba(255, 255, 255, 0.7)';

// Define the default listicle prompt template
const DEFAULT_LISTICLE_PROMPT = `Create a comprehensive listicle article that explores each item in detail. For each list item:

1. Provide a clear explanation of what it is
2. Explain why it's important or relevant
3. Include specific examples or real-world applications
4. Add practical tips or recommendations when applicable

Writing Style:
- Use a conversational yet informative tone
- Break down complex concepts into simple terms
- Include relevant facts and data to support points
- Make the content engaging and actionable
- Maintain consistent length for each list item

Structure Requirements:
- Start with a compelling introduction that sets up the topic
- Each list item should have a clear, descriptive heading
- Include a brief conclusion that summarizes key takeaways
- Format text with appropriate subheadings and spacing`;

// Add LoadingOverlay component
const LoadingOverlay = ({ visible, status, steps }) => {
  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(4px)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: BORDER_COLOR,
          background: CARD_BACKGROUND,
          maxWidth: 500,
          width: '100%',
          mx: 3
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#1F2937',
              fontWeight: 600,
              mb: 2,
              fontSize: '1.125rem'
            }}
          >
            {status}
          </Typography>
          <CircularProgress
            size={60}
            sx={{
              color: '#E04C16',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round'
              }
            }}
          />
        </Box>

        <Box sx={{ width: '100%', px: 3 }}>
          {steps.map((step, index) => (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                opacity: step.started ? 1 : 0.5,
                transition: 'opacity 0.3s ease-in-out'
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: step.status === 'completed' ? '#059669' :
                                step.status === 'error' ? '#DC2626' :
                                step.status === 'pending' ? '#E5E7EB' : '#E5E7EB',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                {step.status === 'completed' ? (
                  <CheckCircle size={16} />
                ) : step.status === 'error' ? (
                  <X size={16} />
                ) : step.status === 'pending' ? (
                  <CircularProgress size={16} sx={{ color: '#6B7280' }} />
                ) : (
                  index + 1
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: step.status === 'error' ? '#DC2626' :
                         step.status === 'completed' ? '#059669' :
                         '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: step.status === 'pending' ? 500 : 400
                }}
              >
                {step.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

const ListicleGenerator = () => {
  const { authenticatedRequest, error: authError, loading: authLoading } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);

  // AI settings
  const [model, setModel] = useState('');

  useEffect(() => {
    const initializeAI = async () => {
      if (availableModels.length > 0) {
        try {
          const keys = await authenticatedRequest(() => apiClient.getApiKeys());
          aiClient.setApiKeys(keys);
          
          // Update AI client with model configurations
          aiClient.updateModelConfigs(availableModels);
          
          // Set initial model if none selected
          if (!model && availableModels.length > 0) {
            const defaultModel = availableModels[0].value;
            console.log('Setting default model:', defaultModel);
            setModel(defaultModel);
          }
    
        } catch (error) {
          console.error('Error initializing AI client:', error);
          setError('Failed to initialize AI services');
        }
      }
    };
  
    initializeAI();
  }, [availableModels, authenticatedRequest, model]);

  // Basic State
  const [title, setTitle] = useState('');
  const [listItems, setListItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [generationSteps, setGenerationSteps] = useState([
    { id: 'init', text: 'Initializing content generation...', status: 'pending', started: false },
    { id: 'content', text: 'Generating article content...', status: 'pending', started: false },
    { id: 'images', text: 'Processing images...', status: 'pending', started: false },
    { id: 'upload', text: 'Publishing to WordPress...', status: 'pending', started: false }
  ]);
  const [generationStatus, setGenerationStatus] = useState('');

  // Response Dialog State
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);

  // WordPress Settings
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');

  // AI Settings
  const [optimizeForSearch, setOptimizeForSearch] = useState(false);

  // Image Settings
  const [includeImages, setIncludeImages] = useState(false);
  const [featuredImage, setFeaturedImage] = useState({
    enabled: false,
    imageModel: 'fal-ai/flux/schnell',
    imageDimension: '1024x1024',
    imageStyle: 'photorealistic'
  });
  
  const [itemImages, setItemImages] = useState(false);
  const [itemImageModel, setItemImageModel] = useState('fal-ai/flux/schnell');
  const [dimension, setDimension] = useState('square');
  const [itemImageStyle, setItemImageStyle] = useState('realistic_image');
  const [featuredImagePrompt, setFeaturedImagePrompt] = useState('');
  const [useAIEnhancement, setUseAIEnhancement] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const imageStyles = [
    { value: 'photorealistic', label: 'Photorealistic' },
    { value: 'digital_art', label: 'Digital Art' },
    { value: 'vector', label: 'Vector Style' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: '3d_render', label: '3D Render' }
  ];

  useEffect(() => {
    if (!authLoading) {
      fetchInitialData();
      initializeServices();
    }
  }, [authLoading]);

  useEffect(() => {
    if (selectedSite) {
      fetchCategories();
    }
  }, [selectedSite]);

  const initializeServices = async () => {
    try {
      const keys = await authenticatedRequest(() => apiClient.getApiKeys());
      
      if (!keys.openai && !keys.anthropic && !keys.fal && !keys.openrouter) {
        throw new Error("No API keys configured. Please add required API keys in settings.");
      }

      if (keys.fal) {
        imageClient.setApiKeys(keys);
        console.log('Image generation services initialized');
      } else {
        console.warn('Fal.ai API key not configured - image generation will be disabled');
        setFeaturedImage(false);
      }
    } catch (error) {
      console.error("Failed to initialize services:", error);
      setError('Failed to initialize services. Please check your API keys in settings.');
    }
  };

  const fetchInitialData = async () => {
    try {
      const sitesData = await authenticatedRequest(() => apiClient.getWordPressSites());
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0].id);
      }
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const fetchCategories = async () => {
    if (!selectedSite) return;
    try {
      const categoriesData = await authenticatedRequest(() => 
        apiClient.getCategories(selectedSite)
      );
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const handleUpdateFeaturedImage = (field, value) => {
    setFeaturedImage(prev => ({
      ...prev,
      [field]: value
    }));
  };
 

    const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };

  const handleGenerateItems = async () => {
    if (!title.trim()) {
      setError('Please enter an article title first');
      return;
    }
  
    setError(null);
  
    try {
      if (!availableModels.length) {
        throw new Error('No AI models available');
      }
  
      const auxModel = getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No auxiliary model available');
      }
  
      // Extract number from title or default to 7
      const titleNumber = title.match(/\d+/);
      const numberOfItems = titleNumber ? parseInt(titleNumber[0]) : 7;
  
      const prompt = `Generate a list of exactly ${numberOfItems} items for an article titled "${title}".
  
  REQUIREMENTS:
  1. Generate EXACTLY ${numberOfItems} items - no more, no less
  2. Each item must be directly related to the title topic
  3. Items should follow a logical progression
  4. Each item should be 3-6 words long
  5. Present items as plain text, one per line
  6. Do not include numbers, bullets, or any other prefixes
  7. Each item must be a complete, standalone topic
  8. Avoid phrases like "Introduction to" or "Overview of"
  
  FORMAT RULES:
  - Return EXACTLY ${numberOfItems} items
  - Return ONLY the list items, nothing else
  - One item per line
  - No blank lines between items
  - No numbering or bullet points
  - No explanatory text
  
  EXAMPLE INPUT:
  "5 Essential Camping Gear Items for Beginners"
  
  EXAMPLE OUTPUT:
  Lightweight All-Season Camping Tent
  Insulated Sleeping Bag and Pad
  Portable Water Filtration System
  Weather-Resistant Camping Backpack
  Multi-Tool and Basic Repair Kit
  
  ACTUAL OUTPUT MUST FOLLOW THIS EXACT FORMAT WITH EXACTLY ${numberOfItems} ITEMS.
  
  NOW GENERATE LIST ITEMS FOR: "${title}"`;
  
      console.log('Using model for item generation:', auxModel);
      console.log('Requesting', numberOfItems, 'items');
      
      const response = await aiClient.generateArticleContent({
        title,
        modelId: getModelId(auxModel),
        customPrompt: prompt
      });
  
      const newItems = response
        .split('\n')
        .filter(line => line.trim())
        .map((text, index) => ({
          id: `item-${Date.now()}-${index}`,
          title: text.replace(/^[-•*\d.]+\s*/, '').trim(), // Remove any prefixes
          notes: '',
          wordCount: 200,
          generateImage: false,
          imagePrompt: '',
          useAIEnhancement: false 
        }));
  
      if (newItems.length !== numberOfItems) {
        throw new Error(`Expected ${numberOfItems} items but received ${newItems.length}. Please try again.`);
      }
  
      setListItems(currentItems => [...currentItems, ...newItems]);
    } catch (err) {
      console.error('List item generation error:', err);
      setError('Failed to generate list items: ' + err.message);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      title: '',
      notes: '',
      wordCount: 200,
      generateImage: false,
      imagePrompt: '',
      imageModel: itemImageModel,
      imageStyle: itemImageStyle,
      imageDimension: dimension,
      useAIEnhancement: false  
    };
    setListItems([...listItems, newItem]);
    setExpandedItem(newItem.id);
  };

  const handleDeleteItem = (id) => {
    setListItems(listItems.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id, field, value) => {
    setListItems(listItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(listItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setListItems(items);
  };

  const getModelId = (selectedModel) => {
    // Map the UI model selection to the actual model ID
    switch (selectedModel) {
      case 'claude':
        return 'claude-3-sonnet-20240222';
      case 'gpt-4':
        return 'gpt-4-turbo-preview';
      default:
        return selectedModel; 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    setGenerationSteps([
      { id: 'init', text: 'Initializing content generation...', status: 'pending' },
      { id: 'content', text: 'Generating article content...', status: 'pending' },
      { id: 'images', text: 'Processing images...', status: 'pending' },
      { id: 'upload', text: 'Publishing to WordPress...', status: 'pending' }
    ]);

    const updateStepStatus = (stepId, status, error = null) => {
      setGenerationSteps(prev => prev.map(step => {
        if (step.id === stepId) {
          return { ...step, status, error, started: true };
        } else if (!step.started && status === 'completed') {
          const currentIndex = prev.findIndex(s => s.id === stepId);
          const thisIndex = prev.findIndex(s => s.id === step.id);
          if (thisIndex === currentIndex + 1) {
            return { ...step, started: true };
          }
        }
        return step;
      }));
    };

    try {
      setGenerationStatus('Starting listicle generation...');
      updateStepStatus('init', 'pending');
      
      // Get WordPress site data first
      const site = sites.find(s => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');

      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);

      // Structure the prompt with emphasis on proper list item formatting
      const structuredPrompt = `Create a detailed listicle article. Format each list item EXACTLY as shown in the examples below.

   INTRODUCTION (Required)
- Write a compelling 2-3 paragraph introduction BEFORE any numbered items
- Do not give the introduction a heading
- Include a broad overview of the topic
- Explain why these items are important
- Set up reader expectations

FORMAT REQUIREMENTS:
1. Do not include the article title at the start
2. Each list item must be formatted either as:
   - ## 1. First Item Name
   - **1. First Item Name**
3. Add two blank lines before each list item
4. Never skip the number prefix for items

EXAMPLE FORMAT:

## 1. First Item Name
First paragraph about this item...

Second paragraph with more details...

## 2. Second Item Name
Content about the second item...

OR 

**1. First Item Name**
First paragraph about this item...

**2. Second Item Name**
Content about the second item...

NOW CREATE THE ARTICLE:

Article Title: ${title}

List Items:
${listItems.map((item, index) => {
  let section = `${index + 1}. ${item.title}`;
  if (item.notes) section += `\nNotes: ${item.notes}`;
  section += `\nTarget word count: ${item.wordCount} words`;
  return section;
}).join('\n\n')}

${optimizeForSearch ? `
Additional Search Optimization Requirements:
- Identify and naturally incorporate semantic variations of the main topic
- Structure content to address common user questions and search intents
- Include relevant LSI keywords naturally
- Ensure comprehensive coverage of related subtopics
- Include clear definitions and explanations of key concepts
- Use natural language that matches search queries
` : ''}`;

      updateStepStatus('init', 'completed');
      updateStepStatus('content', 'pending');

      // Generate content
      setGenerationStatus(`Generating article content for "${title}"`);
      const rawContent = await aiClient.generateArticleContent({
        title,
        modelId: getModelId(model),
        customPrompt: structuredPrompt
      });

      // Let formatContent handle the formatting
      let processedContent = formatContent(rawContent, title, { isListicle: true });

      updateStepStatus('content', 'completed');
      updateStepStatus('images', 'pending');
      let featuredMediaId = null;

      // Handle featured image
      if (featuredImage.enabled) {
        try {
          setGenerationStatus(`Generating featured image for "${title}"`);
          const stylePrompt = {
            photo: 'photorealistic style, high quality, professional photography, no text',
            illustration: 'digital art style, vibrant colors, artistic composition, no text',
            vector: 'vector art style, clean lines, flat design, no text',
            minimalist: 'minimalist style, simple composition, clean design, no text',
            '3d': '3D render style, modern 3D modeling, realistic lighting, no text'
          };
          
          // Use custom prompt if provided, otherwise use style-specific prompt
          const imagePrompt = featuredImage.prompt || stylePrompt[featuredImage.imageStyle];
          const imageData = await imageClient.generateImage({
            prompt: imagePrompt,
            model: featuredImage.imageModel,
            dimension: featuredImage.imageDimension,
            enhancePrompt: !featuredImage.prompt,
            context: {
              articleTitle: title
            }
          });

          if (imageData?.image_url) {
            setGenerationStatus('Uploading featured image...');
            featuredMediaId = await wpClient.uploadFeaturedImage(
              imageData.image_url,
              title
            );
          }
        } catch (error) {
          console.error('Featured image error:', error);
          setGenerationStatus('Featured image generation failed, continuing...');
        }
      }

      // Handle subheading images
      if (listItems.some(item => item.generateImage)) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(processedContent, 'text/html');

        for (const item of listItems) {
          if (!item.generateImage) continue;

          try {
            setGenerationStatus(`Generating image for "${item.title}"`);
            const imageData = await imageClient.generateImage({
              prompt: (item.imagePrompt || item.title) + ", no text",
              model: item.imageModel || itemImageModel,
              dimension: item.imageDimension || dimension,
              style: item.imageStyle || itemImageStyle,
              enhancePrompt: !item.imagePrompt,
              context: {
                articleTitle: title,
                sectionTitle: item.title
              }
            });

            if (imageData?.image_url) {
              const uploadResult = await wpClient.uploadMedia(
                await (await fetch(imageData.image_url)).blob(),
                `subheading-${item.title.replace(/[^a-zA-Z0-9]/g, '-')}.png`
              );

              if (uploadResult) {
                const headings = Array.from(doc.querySelectorAll('h2'));
                const matchingHeading = headings.find(h => {
                  const headingText = h.textContent.toLowerCase().replace(/^[0-9]+\.\s*/, '');
                  const itemText = item.title.toLowerCase();
                  return headingText.includes(itemText) || itemText.includes(headingText);
                });

                if (matchingHeading) {
                  const img = doc.createElement('img');
                  img.src = uploadResult.url;
                  img.alt = item.title;
                  img.className = `wp-image-${uploadResult.id}`;
                  img.style.cssText = 'max-width: 100%; height: auto; margin: 20px 0;';
                  matchingHeading.parentNode.insertBefore(img, matchingHeading.nextSibling);
                }
              }
            }
          } catch (error) {
            console.error(`Failed to process image for "${item.title}":`, error);
            setGenerationStatus(`Image generation failed for "${item.title}", continuing...`);
          }
        }

        processedContent = doc.body.innerHTML;
      }

      updateStepStatus('images', 'completed');
      updateStepStatus('upload', 'pending');

      // Create WordPress post
      setGenerationStatus('Creating WordPress post...');
      const post = await wpClient.createPost({
        title,
        content: processedContent,
        categoryId: category,
        featuredMediaId,
        status: 'draft'
      });

      updateStepStatus('upload', 'completed');
      setGenerationStatus('✓ Article published successfully!');

      setGeneratedArticle({
        title,
        content: processedContent,
        featuredImage: featuredMediaId ? await wpClient.getMediaUrl(featuredMediaId) : null,
        link: post.link,
        postId: post.id
      });
      
      setResponseDialogOpen(true);
      setError(null);

    } catch (err) {
      console.error('Error generating listicle:', err);
      const failedStep = generationSteps.find(step => step.status === 'pending');
      if (failedStep) {
        updateStepStatus(failedStep.id, 'error', err.message);
      }
      
      setError(err.message || 'Failed to generate listicle');
      setGenerationStatus('Generation failed. Please check the error message and try again.');
      setResponseDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <LoadingOverlay
        visible={loading}
        status={generationStatus}
        steps={generationSteps}
      />
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4 
      }}>
        <PageHeader 
          category="Content Creation"
          title="Create A Listicle Article" 
          description="Generate engaging list-based articles by selecting a topic, customizing display settings, and adding optional features like images and descriptions."
          icon={
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
            }}>
              <FileText size={24} color="#FFFFFF" strokeWidth={1.5} />
            </Box>
          }
        />
        <Box sx={{ mr: 6 }}>
          <Button
            onClick={() => setHelpDialogOpen(true)}
            startIcon={<HelpCircle size={18} />}
            sx={{
              bgcolor: 'rgba(59, 130, 246, 0.08)',
              color: '#3B82F6',
              borderRadius: '12px',
              px: 3,
              py: 1,
              minWidth: '140px',
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              border: '1px solid rgba(59, 130, 246, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.12)'
              }
            }}
          >
            How to Use
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main content area */}
        <Grid item xs={12} md={8}>
          {/* Step 1: Basic Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                1
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Basic Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Configure your article title and basic settings
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              {/* Title Input */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Article Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="e.g., Top 10 Ways to Improve Your Productivity"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                    },
                  }}
                />
              </Grid>

              {/* WordPress Settings */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>WordPress Site</InputLabel>
                  <Select
                    value={selectedSite}
                    label="WordPress Site"
                    onChange={(e) => setSelectedSite(e.target.value)}
                    disabled={loading}
                    sx={{
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                    }}
                  >
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={loading || !selectedSite}
                    sx={{
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Step 2: AI Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                2
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  AI Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Choose your AI model and generation settings
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              {/* AI Model Selection */}
              <Grid item xs={12}>
                <ModelSelector
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  required
                  tooltip="Choose the AI model that will generate your listicle content"
                  label="AI MODEL"
                  disabled={loading || modelsLoading}
                  showDescription={true}
                />
              </Grid>

              {/* Search Optimization Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={optimizeForSearch}
                      onChange={(e) => setOptimizeForSearch(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Optimize for Search Intent"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Step 3: Image Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                3
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Image Settings
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Configure featured and list item images
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featuredImage.enabled}
                      onChange={(e) => handleUpdateFeaturedImage('enabled', e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Generate Featured Image"
                />
              </Grid>

              {featuredImage.enabled && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Featured Image Prompt (optional)"
                    placeholder="Leave blank to let AI generate based on article title"
                    value={featuredImage.prompt || ''}
                    onChange={(e) => handleUpdateFeaturedImage('prompt', e.target.value)}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      }
                    }}
                  />
                  <ImageControls
                    model={featuredImage.imageModel}
                    setModel={(value) => handleUpdateFeaturedImage('imageModel', value)}
                    dimension={featuredImage.imageDimension}
                    setDimension={(value) => handleUpdateFeaturedImage('imageDimension', value)}
                    style={featuredImage.imageStyle}
                    setStyle={(value) => handleUpdateFeaturedImage('imageStyle', value)}
                    disabled={!featuredImage.enabled}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Step 4: List Items */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                4
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  List Items
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Generate and manage your list items
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                onClick={handleGenerateItems}
                startIcon={<Wand2 />}
                variant="outlined"
                disabled={!title || loading}
                sx={{
                  flexGrow: 1,
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#3B82F6',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  },
                }}
              >
                {loading ? 'Generating...' : 'Generate List Items'}
              </Button>
              <Button
                onClick={handleAddItem}
                startIcon={<Plus />}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#3B82F6',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  },
                }}
              >
                Add Custom Item
              </Button>
              <Button
                onClick={() => setClearDialogOpen(true)}
                color="error"
                variant="outlined"
                disabled={listItems.length === 0}
                startIcon={<Trash2 />}
                sx={{
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  color: '#3B82F6',
                  '&:hover': {
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  },
                }}
              >
                Clear All
              </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="listItems">
                {(provided) => (
                  <List
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ 
                      bgcolor: 'background.paper',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: BORDER_COLOR,
                      p: 2
                    }}
                  >
                    {listItems.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(provided) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: BORDER_COLOR,
                              '&:last-child': {
                                borderBottom: 'none'
                              },
                              p: 2,
                              mb: 1,
                              borderRadius: '8px',
                              backgroundColor: CARD_BACKGROUND,
                              '&:hover': {
                                backgroundColor: alpha('#6B7280', 0.02),
                              }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'flex-start',
                              width: '100%'
                            }}>
                              <Box
                                {...provided.dragHandleProps}
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  mr: 2,
                                  cursor: 'grab'
                                }}
                              >
                                <GripVertical size={20} color="#6B7280" />
                              </Box>

                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <TextField
                                    fullWidth
                                    placeholder={`Item ${index + 1}`}
                                    value={item.title}
                                    onChange={(e) => handleUpdateItem(
                                      item.id,
                                      'title',
                                      e.target.value
                                    )}
                                    variant="standard"
                                    sx={{ mr: 2 }}
                                  />
                                  <Tooltip title="Expand item settings">
                                    <IconButton
                                      onClick={() => setExpandedItem(
                                        expandedItem === item.id ? null : item.id
                                      )}
                                      size="small"
                                    >
                                      {expandedItem === item.id ?
                                        <ChevronDown size={20} color="#6B7280" /> :
                                        <ChevronRight size={20} color="#6B7280" />
                                      }
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Generate image for this item">
                                    <IconButton
                                      onClick={() => handleUpdateItem(
                                        item.id,
                                        'generateImage',
                                        !item.generateImage
                                      )}
                                      size="small"
                                      color={item.generateImage ? "primary" : "default"}
                                    >
                                      <Image size={20} color={item.generateImage ? "#E04C16" : "#6B7280"} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete item">
                                    <IconButton
                                      onClick={() => handleDeleteItem(item.id)}
                                      size="small"
                                      color="error"
                                    >
                                      <Trash2 size={20} color="#DC2626" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>

                                <Collapse in={expandedItem === item.id}>
                                  <Box sx={{ mt: 2 }}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={2}
                                      label="Item Notes (optional)"
                                      placeholder="Add key points or details for this list item..."
                                      value={item.notes}
                                      onChange={(e) => handleUpdateItem(
                                        item.id,
                                        'notes',
                                        e.target.value
                                      )}
                                      sx={{ mb: 2 }}
                                    />
                                    <TextField
                                      type="number"
                                      label="Target Word Count"
                                      value={item.wordCount}
                                      onChange={(e) => handleUpdateItem(
                                        item.id,
                                        'wordCount',
                                        parseInt(e.target.value) || 0
                                      )}
                                      InputProps={{ inputProps: { min: 50 } }}
                                      sx={{ mb: 2 }}
                                    />
                                    {item.generateImage && (
                                      <Box sx={{ mt: 2 }}>
                                        <ImageControls
                                          model={item.imageModel || itemImageModel}
                                          setModel={(value) => handleUpdateItem(item.id, 'imageModel', value)}
                                          style={item.imageStyle || itemImageStyle}
                                          setStyle={(value) => handleUpdateItem(item.id, 'imageStyle', value)}
                                          dimension={item.imageDimension || dimension}
                                          setDimension={(value) => handleUpdateItem(item.id, 'imageDimension', value)}
                                          prompt={item.imagePrompt}
                                          setPrompt={(value) => handleUpdateItem(item.id, 'imagePrompt', value)}
                                          loading={loading}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </Box>
                            </Box>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>

          {/* Step 5: Generate */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{ 
                minWidth: 40,
                height: 40,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.2)',
                fontSize: '1rem',
                fontWeight: 700
              }}>
                5
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1F2937',
                    position: 'relative',
                    display: 'inline-block',
                    fontSize: '1.125rem',
                    lineHeight: 1.2,
                    pb: 0.5
                  }}
                >
                  Generate Article
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.875rem' }}
                >
                  Generate and publish your listicle article
                </Typography>
              </Box>
            </Box>
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Error Display */}
                <Grid item xs={12}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!title || listItems.length === 0 || loading}
                    sx={{
                      background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                      },
                    }}
                  >
                    {loading ? 'Generating Listicle...' : 'Generate Listicle'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3,
              height: '100%',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: BORDER_COLOR,
              background: CARD_BACKGROUND,
              overflow: 'hidden'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#1F2937',
                fontSize: '1.125rem',
                mb: 2
              }}
            >
              Article Preview
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                color="primary" 
                sx={{ 
                  mb: 3,
                  fontSize: '1.125rem',
                  fontWeight: 600
                }}
              >
                {title || 'Your Listicle Title'}
              </Typography>
              {listItems.length > 0 ? (
                <List>
                  {listItems.map((item, index) => (
                    <ListItem 
                      key={item.id} 
                      sx={{ 
                        px: 0,
                        mb: 2,
                        display: 'block',
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: CARD_BACKGROUND,
                        border: '1px solid',
                        borderColor: BORDER_COLOR,
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontSize: '1rem',
                          fontWeight: 500,
                          mb: 1,
                          color: '#1F2937'
                        }}
                      >
                        {`${index + 1}. ${item.title || 'Untitled Item'}`}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {item.notes && (
                          <>
                            Notes: {item.notes}
                            <br />
                          </>
                        )}
                        Target: {item.wordCount} words
                        {item.generateImage && (
                          <>
                            <br />
                            {item.imagePrompt ? 
                              `Custom image prompt: ${item.imagePrompt}` : 
                              'AI-generated image enabled'
                            }
                          </>
                        )}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography 
                  color="text.secondary" 
                  sx={{ fontSize: '0.875rem' }}
                >
                  No list items added yet. Click "Add List Item" to get started.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          p: 3,
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          textAlign: 'center'
        }}>
          <Box sx={{ 
            width: 70, 
            height: 70, 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 2,
            background: error ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.2))' : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.2))',
            color: error ? '#EF4444' : '#10B981',
            boxShadow: error ? '0 8px 16px rgba(239, 68, 68, 0.15)' : '0 8px 16px rgba(16, 185, 129, 0.15)',
            animation: !error ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.4)'
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)'
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)'
              }
            }
          }}>
            {error ? (
              <AlertCircle size={36} strokeWidth={2} />
            ) : (
              <CheckCircle size={36} strokeWidth={2} />
            )}
          </Box>
          
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            mb: 1,
            color: '#1F2937',
            fontSize: '1.5rem'
          }}>
            {error ? 'Generation Failed' : 'Listicle Generated Successfully'}
          </Typography>
             
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            mb: 3,
            maxWidth: '90%',
            lineHeight: 1.6
          }}>
            {error 
              ? error || 'An error occurred while generating your listicle.'
              : 'Your listicle has been successfully created and saved as a draft in WordPress.'}
          </Typography>
          
          <Box sx={{ 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            mt: 1
          }}>
            {!error && (
              <Button 
                onClick={() => {
                  setResponseDialogOpen(false);
                  setPreviewDialogOpen(true);
                }}
                variant="outlined"
                startIcon={<Eye size={18} />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  color: '#1F2937',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#3B82F6',
                    bgcolor: 'rgba(59, 130, 246, 0.04)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Preview Article
              </Button>
            )}
            <Button
              onClick={() => setResponseDialogOpen(false)}
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '12px',
                bgcolor: '#3B82F6',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transition: 'all 0.6s ease',
                },
                '&:hover': {
                  bgcolor: '#2563EB',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                  '&:before': {
                    left: '100%',
                  }
                }
              }}
            >
              {error ? 'Try Again' : 'Create Another Listicle'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '80vh',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: BORDER_COLOR,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: BORDER_COLOR,
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
            Article Preview
          </Typography>
          <IconButton 
            onClick={() => setPreviewDialogOpen(false)} 
            size="small"
            sx={{
              color: '#6B7280',
              '&:hover': {
                backgroundColor: alpha('#6B7280', 0.04),
              }
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {generatedArticle && (
            <>
              <Typography variant="h4" sx={{ mb: 2, fontSize: '1.5rem', fontWeight: 700 }}>
                {generatedArticle.title}
              </Typography>
              
              {generatedArticle.featuredImage && (
                <Box sx={{ my: 2 }}>
                  <img 
                    src={generatedArticle.featuredImage} 
                    alt={generatedArticle.title}
                    style={{ 
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }} 
                  />
                </Box>
              )}

              <Box 
                sx={{ mt: 3 }}
                dangerouslySetInnerHTML={{ __html: generatedArticle.content }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: '1px solid',
          borderColor: BORDER_COLOR,
          px: 3,
          py: 2
        }}>
          <Button 
            onClick={() => setPreviewDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: 'rgba(59, 130, 246, 0.2)',
              color: '#3B82F6',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                backgroundColor: 'rgba(59, 130, 246, 0.04)',
              },
            }}
          >
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid',
            borderColor: BORDER_COLOR,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1.125rem', fontWeight: 600 }}>
          Clear All List Items?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
            Are you sure you want to delete all list items? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setClearDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: 'rgba(59, 130, 246, 0.2)',
              color: '#3B82F6',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                backgroundColor: 'rgba(59, 130, 246, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setListItems([]);
              setClearDialogOpen(false);
            }}
            variant="contained"
            color="error"
            sx={{
              background: 'linear-gradient(45deg, #DC2626, #EF4444)',
              '&:hover': {
                background: 'linear-gradient(45deg, #B91C1C, #DC2626)',
              },
            }}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
          }}>
            <HelpCircle size={24} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>How to Use Listicle Generator</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setHelpDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
          }}
        >
          <X size={20} />
        </IconButton>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              The Listicle Generator helps you create engaging list-based articles with ease. Follow these steps to create professional content that resonates with your audience.
            </Typography>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 1: Basic Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Start by configuring your article's basic settings:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Article Title:</strong> Enter a descriptive and engaging title (e.g., "10 Essential Tips for Beginner Gardeners")
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>WordPress Site:</strong> Select where you want to publish your article
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Category:</strong> Choose the appropriate category for your article
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 2: AI Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Configure your AI generation settings:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>AI Model:</strong> Select your preferred AI model for content generation
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Search Optimization:</strong> Enable to optimize content for search engines
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 3: Image Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Configure featured and list item images:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Generate Featured Image:</strong> Enable to generate a featured image for your article
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Featured Image Prompt:</strong> Enter a custom prompt for the featured image
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Image Model:</strong> Choose the AI model for generating the featured image
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Image Dimension:</strong> Choose the dimension for the featured image
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Use AI Enhancement:</strong> Enable to enhance the image with AI
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 4: List Items
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Create and customize your list items:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Generate Items:</strong> Click to automatically create items based on your title
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Add Custom Items:</strong> Manually add items with custom titles and settings
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Customize Items:</strong> For each item, you can:
                  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                    <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Add notes to guide content generation
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Set target word count
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                      Enable image generation with custom settings
                    </Typography>
                  </Box>
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Reorder Items:</strong> Drag and drop items to arrange them in your preferred order
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
                Step 5: Generate Article
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                When you're ready, click "Generate Listicle" to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Generate Content:</strong> Create well-structured content for each list item
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Process Images:</strong> Generate and place images as requested
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  <strong>Publish to WordPress:</strong> Save the article as a draft in your selected site
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              Tip: Use the preview feature to review your article before finalizing. You can make adjustments and regenerate if needed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setHelpDialogOpen(false)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4, 
              py: 1.2,
              background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
              boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #CC4414, #E85E35)',
              }
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListicleGenerator;